import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Project } from 'src/services/projectsService';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';

// The lead created in Bigin (the CRM) at sign-up, and the details collected for it.
export interface OnboardingBigin {
  pipelineId: string | number | null;
  data: { companyName?: string; CRM?: string; Requests?: string; [key: string]: unknown } | null;
}

// Persisted, so the shape must stay compatible with stored state.
export interface OnboardingState {
  currentStep: number | null;
  promptType: 'system' | 'manual' | null;
  bigin: OnboardingBigin;
  // The project and knowledge base created during onboarding ({ id: null } until then).
  project: Partial<Project> & { id: Project['id'] | null };
  knowledgeBase: Partial<KnowledgeBase> & { id: KnowledgeBase['id'] | null };
  success: boolean;
}

const initialState: OnboardingState = {
  currentStep: null,
  promptType: null,
  bigin: {
    pipelineId: null,
    data: null,
  },
  project: {
    id: null,
  },
  knowledgeBase: {
    id: null,
  },
  success: false,
};

const onboardingSlice = createSlice({
  name: 'onboardingSlice',
  initialState,
  reducers: {
    setBigin: (state, action: PayloadAction<OnboardingBigin>) => {
      state.bigin = action.payload;
    },
    setProject: (state, action: PayloadAction<OnboardingState['project']>) => {
      state.project = action.payload;
    },
    setKnowledgeBase: (state, action: PayloadAction<OnboardingState['knowledgeBase']>) => {
      state.knowledgeBase = action.payload;
    },
    setPromptType: (state, action: PayloadAction<OnboardingState['promptType']>) => {
      state.promptType = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<number | null>) => {
      state.currentStep = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      return { ...initialState, success: action.payload };
    },
    clearOnboardingState: () => {
      return { ...initialState };
    },
  },
});

export const {
  setBigin,
  setProject,
  setKnowledgeBase,
  setCurrentStep,
  setSuccess,
  clearOnboardingState,
  setPromptType,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
